import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { COMPANY, SERVICES, ZONES } from "../lib/data";
import { CHARENTE } from "../lib/ads";
import { DEPARTMENTS } from "../lib/departments";
import { LogoMark } from "./Logo";

export default function Footer() {
  const { pathname } = useLocation();
  const isCharenteAds = /-charente$/.test(pathname);
  // Détection page département : /plombier-<slug>, /serrurier-<slug>, /electricien-<slug>
  const deptMatch = pathname.match(/^\/(?:plombier|serrurier|electricien)-([a-z-]+)$/);
  const deptSlug = deptMatch ? deptMatch[1] : null;
  const dept = deptSlug && DEPARTMENTS[deptSlug] ? DEPARTMENTS[deptSlug] : null;

  let zoneLabel = COMPANY.zone;
  let zonesList = ZONES;
  if (isCharenteAds) {
    zoneLabel = CHARENTE.name;
    zonesList = CHARENTE.cities;
  } else if (dept) {
    zoneLabel = dept.label;
    zonesList = dept.cities;
  }
  return (
    <footer data-testid="footer" className="bg-ink text-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-5">
              <LogoMark size={38} className="rounded-[11px]" />
              <span className="font-display text-lg font-extrabold">{COMPANY.name}</span>
            </div>
            <p className="text-white/60 max-w-sm leading-relaxed">
              Dépannage d&apos;urgence en plomberie, serrurerie et électricité. {zoneLabel},
              24h/24 et 7j/7.
            </p>
            <a href={COMPANY.phoneHref} className="btn-primary mt-7">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>

          <div>
            <h4 className="font-display font-bold mb-5">Services</h4>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${s.slug}`}
                    className="text-white/60 hover:text-white transition-colors duration-200"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold mb-5">Contact</h4>
            <ul className="space-y-4 text-white/60">
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-1 text-brand" /> {COMPANY.email}
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-1 text-brand" /> {zoneLabel}
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-4 w-4 mt-1 text-brand" /> {COMPANY.hours}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10">
          <h4 className="font-display font-bold mb-5 text-white/90">Nos interventions par département en Île-de-France</h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 text-sm">
            {[
              { slug: "plombier",    label: "Plombier"    },
              { slug: "serrurier",   label: "Serrurier"   },
              { slug: "electricien", label: "Électricien" },
            ].map((m) => (
              <div key={m.slug}>
                <p className="text-white/70 font-semibold mb-3">{m.label}</p>
                <ul className="space-y-2">
                  {Object.entries(DEPARTMENTS).map(([slug, d]) => (
                    <li key={slug}>
                      <Link
                        to={`/${m.slug}-${slug}`}
                        className="text-white/55 hover:text-white transition-colors"
                      >
                        {m.label} {d.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10">
          <p className="text-white/40 text-sm mb-4">
            <span className="text-white/70 font-semibold">Zones d&apos;intervention :</span>{" "}
            {zonesList.join(" · ")}
          </p>
          <div className="flex flex-col md:flex-row justify-between gap-3 text-white/40 text-sm">
            <span>© {new Date().getFullYear()} {COMPANY.name}. Tous droits réservés.</span>
            <span>Artisans certifiés & assurés — Devis sans surprise</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
